import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { appCurrentBranding } from '../../../config/branding';
import { branding } from '../../../config/enum';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { AccountFacilityAddState } from '../../../interface/account-management-facility.interface';
import { AccountParameters } from '../../../interface/account-management.interface';
import { useAppDispatch } from '../../../redux/hooks';
import { fetchAllAccounts } from '../../../redux/slices/AccountManagementSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomFileInput from '../../../shared-components/custom-file-input/CustomFileInput';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../shared-components/custom-input/CustomPhoneInput';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import getErrorMessageFromConcatenatedJSON from '../../../shared-functions/getErrorMessageFromConcatenatedJSON';
import {
  checkFileSizeValidation,
  emailValidation,
  passwordValidation,
  phoneNumberValidation,
  stringValidation,
} from '../../../shared-functions/Validation';
import accountApi from '../../../utils/api/account';

/**
 * Add Account function component
 * @param param0
 * @returns
 */
function AddFacilityAccount({
  closeModal,
  sidebarClosed,
}: {
  closeModal: (value: string) => void;
  sidebarClosed: () => void;
}) {
  const { t } = useTranslation('account');
  const dispatch = useAppDispatch();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [showFormErrors, setShowFormErrors] = useState<boolean>(false);
  const [accountDetails, setAccountDetails] = useState<
    AccountFacilityAddState & { type: 'email' | 'phone' }
  >({
    name: '',
    accountName: '',
    governmentId: [],
    email: '',
    phone: '',
    password: '',
    rePassword: '',
    type: 'email',
    countryCode: appCurrentBranding === branding.VOLTIE ? '1' : '966',
  });

  /**
   * Handle the change function
   * @param name
   * @param value
   */
  const handleChangeFunction = (name: string, value: string[] | File[] | string | number) => {
    if (name === 'type') {
      setAccountDetails((previous) => ({
        ...previous,
        type: value as 'email' | 'phone',
        email: '',
        phone: '',
      }));
    } else {
      setAccountDetails((previous) => ({
        ...previous,
        [name]: value,
      }));
    }
  };

  /**
   * Handle the submit function
   */
  const handleSubmit = () => {
    setShowFormErrors(true);
    let errorMessage = false;
    if (
      (process.env.REACT_APP_BRANDING !== branding.VOLTIE &&
        !checkFileSizeValidation(accountDetails.governmentId, 5, 1)) ||
      (!accountDetails.email && !accountDetails.phone) ||
      (accountDetails.email && !emailValidation(accountDetails.email)) ||
      (accountDetails.phone && !phoneNumberValidation(accountDetails.phone)) ||
      !stringValidation(1, 100, accountDetails.name) ||
      !passwordValidation(accountDetails.password) ||
      accountDetails.rePassword !== accountDetails.password
    ) {
      errorMessage = true;
    }
    if (!errorMessage) {
      setDisabled(true);
      const request: AccountParameters = {
        name: accountDetails.name,
        userName: accountDetails.name,
        password: accountDetails.password,
        governmentId: accountDetails.governmentId[0],
        accountName: accountDetails.accountName,
      };
      if (accountDetails.phone) {
        request.phoneNumber = accountDetails.phone;
        request.countryCode = accountDetails.countryCode;
      }
      if (accountDetails.email) {
        request.email = accountDetails.email;
      }
      accountApi
        .createAccounts(request)
        .then((response: any) => {
          getErrorMessageFromConcatenatedJSON(response.data);
          setDisabled(true);
          dispatch(
            fetchAllAccounts({
              page: DefaultApiParameters.page,
              limit: DefaultApiParameters.limit,
            }),
          );
          closeModal(t('accountAddedSuccessfully'));
          sidebarClosed();
        })
        .catch((error) => {
          setDisabled(false);
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message || error?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * Handle the government id remove function
   * @param name
   * @param url
   * @param indexToRemove
   * @param type
   */
  const handleRemoveFile = (
    name: string,
    url: string,
    indexToRemove: number,
    type: 'newFile' | 'existingFile',
  ) => {
    if (type === 'newFile') {
      const governmentId = accountDetails?.governmentId;
      governmentId.splice(indexToRemove, 1);
      setAccountDetails((previous) => ({
        ...previous,
        governmentId,
      }));
    }
  };

  return (
    <div className="account-form">
      <div className="form-header">
        <h3>{t('addFacility')}</h3>
      </div>
      <div
        className="form-body"
        style={{ overflowY: 'scroll', height: window.innerHeight >= 700 ? 347 : 352 }}>
        <div className="form-row">
          <div className="form-field">
            <CustomInput
              name="accountName"
              inputLabel={t('accountName')}
              type="text"
              placeholder={t('enterAccountName')}
              value={accountDetails.accountName}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={showFormErrors && !stringValidation(1, 100, accountDetails.accountName)}
              errorMessage={t('accountNameRequired')}
            />
          </div>
          <div className="form-field">
            <CustomInput
              name="name"
              inputLabel={t('userName')}
              type="text"
              placeholder={t('enterUserName')}
              value={accountDetails.name}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={showFormErrors && !stringValidation(1, 100, accountDetails.name)}
              errorMessage={t('userNameRequired')}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <CustomFileInput
              name="governmentId"
              inputLabel={t('governmentId')}
              placeholder={t('uploadHere')}
              selectedFiles={accountDetails.governmentId}
              existingName="governmentIdOld"
              onChangeValue={handleChangeFunction}
              unselectHandler={handleRemoveFile}
              acceptedFileType=""
              valueRequired={process.env.REACT_APP_BRANDING !== 'voltie'}
              error={
                process.env.REACT_APP_BRANDING !== 'voltie'
                  ? showFormErrors && accountDetails.governmentId.length !== 1
                  : false
              }
              errorMessage={
                process.env.REACT_APP_BRANDING !== 'voltie' ? t('governmentIdRequired') : ''
              }
            />
          </div>
        </div>
        <div className="radio-button-wrap">
          <RadioButton
            name="type"
            value={accountDetails.type}
            list={[
              { label: t('email'), value: 'email' },
              { label: t('phone'), value: 'phone' },
            ]}
            onChangeValue={handleChangeFunction}
          />
        </div>

        <div className="radio-button-wrap">
          {accountDetails.type === 'email' && (
            <div className="form-field">
              <CustomInput
                name="email"
                inputLabel={t('emailId')}
                type="text"
                placeholder={t('enterEmail')}
                value={accountDetails.email}
                onChangeValue={handleChangeFunction}
                valueRequired
                error={
                  showFormErrors &&
                  (accountDetails.email === '' || !emailValidation(accountDetails.email))
                }
                errorMessage={t('emailRequiredOrInvalid')}
              />
            </div>
          )}
          {accountDetails.type === 'phone' && (
            <div className="form-field">
              <CustomPhoneInput
                name="phone"
                inputLabel={t('phoneNumber')}
                type="number"
                placeholder={t('enterPhoneNumber')}
                value={accountDetails.phone}
                value1={accountDetails.countryCode}
                onChangeValue={handleChangeFunction}
                valueRequired
                error={
                  showFormErrors &&
                  (accountDetails.phone === '' || !phoneNumberValidation(accountDetails.phone))
                }
                errorMessage={t('phoneRequiredOrInvalid')}
              />
            </div>
          )}
        </div>

        <hr className="form-divider" />

        <div className="form-row">
          <div className="form-field">
            <CustomInput
              name="password"
              inputLabel={t('setPassword')}
              type="password"
              placeholder={t('enterPassword')}
              value={accountDetails.password}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={showFormErrors && !passwordValidation(accountDetails.password)}
              errorMessage={t('passwordRequiredOrInvalid')}
            />
          </div>
          <div className="form-field">
            <CustomInput
              name="rePassword"
              inputLabel={t('reEnterPassword')}
              type="password"
              placeholder={t('enterPassword')}
              value={accountDetails.rePassword}
              onChangeValue={handleChangeFunction}
              valueRequired
              error={
                showFormErrors &&
                !passwordValidation(accountDetails.rePassword) &&
                (accountDetails.password !== accountDetails.rePassword ||
                  accountDetails.rePassword === '')
              }
              errorMessage={
                accountDetails.rePassword.length === 0
                  ? t('reEnterPasswordRequiredOrInvalid')
                  : t('passwordsMustMatch')
              }
            />
          </div>
        </div>
      </div>
      <div className="form-bottom-button">
        <div className="table-button-item">
          <MyButton
            label={t('cancel')}
            buttonType="outline"
            onClickFunc={() => {
              closeModal('');
              sidebarClosed();
            }}
          />
        </div>
        <div className="table-button-item">
          <MyButton
            label={t('submit')}
            buttonType="submit"
            loading={disabled}
            disabled={disabled}
            onClickFunc={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}

export default AddFacilityAccount;
